import { StorageServiceFactory } from "@/services/storage/StorageServiceFactory";

/**
 * Quick S3 test to identify the exact issue
 */
export const quickS3Test = async (craftspaceId: string = "ws1r149upxf") => {
  console.log("🔍 Quick S3 Test Starting...");

  try {
    // 1. Test storage service creation
    console.log("1️⃣ Creating storage service...");
    const storageService = await StorageServiceFactory.createStorageService(craftspaceId);
    console.log("✅ Storage service created successfully");

    // 2. Test connection
    console.log("2️⃣ Testing connection...");
    const connectionResult = await storageService.testConnection();
    console.log("Connection result:", connectionResult);

    // 3. Generate a test signed URL
    console.log("3️⃣ Generating test signed URL...");
    const testPath = "ws1r149upxf/prjgmy0p0jjw/panoramas/p1-d1/compressed_p1-d1.webp";
    const signedUrl = await storageService.getFileUrl(testPath);
    console.log("✅ Signed URL generated:", signedUrl);

    // 4. Test the signed URL with lightweight GET request
    console.log("4️⃣ Testing signed URL with lightweight GET request...");
    try {
      const response = await fetch(signedUrl, {
        method: "GET",
        headers: { Range: "bytes=0-1" }, // Only fetch first 2 bytes
      });
      console.log("GET response:", {
        status: response.status,
        statusText: response.statusText,
        headers: Object.fromEntries(response.headers.entries()),
      });

      if (response.status === 403) {
        console.error("🚨 ISSUE IDENTIFIED: S3/MinIO server is rejecting valid signed URLs");
        console.error("This suggests:");
        console.error(
          "  • S3 credentials may be correct for URL generation but not for actual access",
        );
        console.error("  • MinIO server clock might be out of sync");
        console.error("  • S3 bucket policies may be restricting access");
        console.error("  • MinIO server configuration issue");
      }
    } catch (fetchError) {
      console.error("❌ Fetch error:", fetchError);
    }

    // 5. Check system time vs server time
    console.log("5️⃣ Checking time synchronization...");
    const localTime = new Date();
    try {
      const timeResponse = await fetch("https://worldtimeapi.org/api/timezone/UTC");
      const timeData = await timeResponse.json();
      const serverTime = new Date(timeData.datetime);
      const timeDiff = Math.abs(localTime.getTime() - serverTime.getTime());

      console.log("Time check:", {
        localTime: localTime.toISOString(),
        serverTime: serverTime.toISOString(),
        differenceMs: timeDiff,
        differenceMinutes: Math.round(timeDiff / 60000),
      });

      if (timeDiff > 300000) {
        // 5 minutes
        console.warn("⚠️ POTENTIAL ISSUE: System clock is off by more than 5 minutes");
      } else {
        console.log("✅ System time looks good");
      }
    } catch (_timeError) {
      console.warn("⚠️ Could not check time synchronization");
    }
  } catch (error) {
    console.error("❌ Test failed:", error);
  }
};

// Make it globally available
const globalWindow = window as Window & { quickS3Test?: typeof quickS3Test };
globalWindow.quickS3Test = quickS3Test;
